import { useEffect, useRef, useState, type ComponentProps, type FormEvent } from 'react';

import { Dialog } from '@goshippo/libraries/Dialog';

import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '~/src/components/LoadingButton';
import { useEmailVerification } from '~/src/hooks/useEmailVerification';

import { CODE_LENGTH, CodeInput } from '../CodeInput/CodeInput';
import lockIcon from './lock.svg';

type OtcVerificationDialogProps = {
  email?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const OTC_VERIFICATION_FORM_ID = 'otc-verification-form';

export const OtcVerificationDialog = ({
  email,
  isOpen,
  onClose,
  onSuccess,
}: OtcVerificationDialogProps) => {
  const { t } = useTranslation('email-verification');
  const [code, setCode] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const canAutoSubmit = useRef(true);

  const {
    clearError,
    errorMessage,
    isResendCodeLoading,
    isSendCodeLoading,
    isVerifyingCode,
    sendCode,
    verifyCode,
  } = useEmailVerification({
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  useEffect(() => {
    if (isOpen) {
      sendCode();
      canAutoSubmit.current = true;
    } else {
      setCode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOnClose: ComponentProps<typeof Dialog>['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (code.length === CODE_LENGTH) verifyCode(code);
  };

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);

    if (errorMessage) {
      clearError();
      return;
    }

    if (newCode.length === CODE_LENGTH && canAutoSubmit.current) {
      verifyCode(newCode);
      canAutoSubmit.current = false;
    }
  };

  const dialogContent = (
    <Stack
      component="form"
      direction={{ sm: 'row' }}
      gap={2}
      id={OTC_VERIFICATION_FORM_ID}
      onSubmit={handleSubmit}
    >
      <Box
        alt="Image of a lock"
        component="img"
        height={120}
        marginX="auto"
        src={lockIcon}
        width={120}
      />

      <Stack gap={3}>
        <div>
          <Typography>{t('otc.instruction')}</Typography>
          <Typography fontWeight="bold">{email}</Typography>
        </div>
        <CodeInput key={resetKey} errorMessage={errorMessage} onCodeChange={handleCodeChange} />
      </Stack>
    </Stack>
  );

  return (
    <Dialog
      actions={[
        <LoadingButton
          color="secondary"
          disabled={isVerifyingCode || isSendCodeLoading}
          isLoading={isResendCodeLoading}
          key="resend"
          onClick={() => {
            sendCode(true);
            setCode('');
            setResetKey((prev) => prev + 1);
            canAutoSubmit.current = true;
          }}
          variant="text"
        >
          {t('otc.resendCode')}
        </LoadingButton>,
        <LoadingButton
          disabled={code.length !== CODE_LENGTH || isSendCodeLoading || isResendCodeLoading}
          form={OTC_VERIFICATION_FORM_ID}
          isLoading={isVerifyingCode}
          key="verify"
          type="submit"
        >
          {t('otc.verify')}
        </LoadingButton>,
      ]}
      content={dialogContent}
      fitContent
      onClose={handleOnClose}
      open={isOpen}
      title={t('otc.title')}
    />
  );
};
